import { useEffect, useRef, useState, type ReactElement } from 'react';

interface AutoSizeProps {
  children: (size: { width: number; height: number }) => ReactElement;
  minHeight?: number;
}

/**
 * Measures its own box with a ResizeObserver and hands the size to the chart, so charts size
 * synchronously inside grid cells (recharts' ResponsiveContainer debounces and paints late).
 */
export const AutoSize = ({ children, minHeight = 120 }: AutoSizeProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const element = ref.current;
    if (!element) {
      return undefined;
    }
    const measure = () => {
      const { width, height } = element.getBoundingClientRect();
      setSize({ width: Math.floor(width), height: Math.floor(height) });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} style={{ width: '100%', height: '100%', minHeight }}>
      {size.width > 0 && size.height > 0 ? children(size) : null}
    </div>
  );
};
